import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  FileText,
  Eye,
  TrendingUp,
  Calendar,
  Trophy,
  BarChart3,
  Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WriterStats({ writer, token, onBack }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [showCustom, setShowCustom] = useState(false);

  const fetchStats = async (startDate, endDate) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);

      const response = await axios.get(
        `${API}/writers/${writer.slug}/stats?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load writer statistics");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (period === "custom" && customStart && customEnd) {
      fetchStats(customStart, customEnd);
    } else if (period !== "custom") {
      const days = parseInt(period);
      const endDate = new Date().toISOString();
      const startDate = new Date(
        Date.now() - days * 24 * 60 * 60 * 1000
      ).toISOString();
      fetchStats(startDate, endDate);
    }
  }, [period, writer.slug]);

  const handlePeriodChange = (value) => {
    setPeriod(value);
    setShowCustom(value === "custom");
  };

  const handleCustomSearch = () => {
    if (customStart && customEnd) {
      fetchStats(
        new Date(customStart).toISOString(),
        new Date(customEnd).toISOString()
      );
    }
  };

  // Mini bar chart component
  const MiniChart = ({ data, dataKey, color, label }) => {
    if (!data || data.length === 0) {
      return (
        <div className="h-32 flex items-center justify-center text-zinc-400 text-sm">
          No data for this period
        </div>
      );
    }

    const maxValue = Math.max(...data.map((d) => d[dataKey]));
    const chartData = data.slice(-14); // Last 14 data points

    return (
      <div className="space-y-2">
        <div className="flex items-end gap-1 h-24">
          {chartData.map((item, index) => {
            const height = maxValue > 0 ? (item[dataKey] / maxValue) * 100 : 0;
            return (
              <div
                key={index}
                className="flex-1 flex flex-col items-center group relative"
              >
                <div
                  className={`w-full ${color} rounded-t transition-all hover:opacity-80`}
                  style={{ height: `${Math.max(height, 4)}%` }}
                />
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-zinc-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  {item.date}: {item[dataKey]} {label}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-xs text-zinc-400">
          <span>{chartData[0]?.date?.slice(5)}</span>
          <span>{chartData[chartData.length - 1]?.date?.slice(5)}</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-[#FF66C4]" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-zinc-500">Failed to load statistics</p>
        <Button onClick={onBack} variant="outline" className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="writer-stats-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="rounded-none"
            data-testid="back-to-writers-btn"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Writers
          </Button>
        </div>
      </div>

      {/* Writer Profile Header */}
      <div className="bg-white border border-zinc-200 p-6">
        <div className="flex items-center gap-6">
          {stats.writer.profile_image ? (
            <img
              src={stats.writer.profile_image}
              alt={stats.writer.name}
              className="w-20 h-20 object-cover"
            />
          ) : (
            <div className="w-20 h-20 bg-zinc-100 flex items-center justify-center text-2xl font-bold text-zinc-400">
              {stats.writer.name.charAt(0)}
            </div>
          )}
          <div>
            <h1 className="font-serif text-3xl font-bold">{stats.writer.name}</h1>
            <p className="text-zinc-500 mt-1">{stats.writer.bio || "Writer"}</p>
          </div>
        </div>
      </div>

      {/* Period Selector */}
      <div className="bg-white border border-zinc-200 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-2">
            <Label>Time Period</Label>
            <Select value={period} onValueChange={handlePeriodChange}>
              <SelectTrigger className="w-48 rounded-none" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {showCustom && (
            <>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="rounded-none"
                  data-testid="custom-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="rounded-none"
                  data-testid="custom-end-date"
                />
              </div>
              <Button
                onClick={handleCustomSearch}
                className="rounded-none bg-[#FF66C4] hover:bg-[#e55ab0]"
                data-testid="apply-custom-range"
              >
                Apply
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-zinc-200 p-6" data-testid="stat-total-articles">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="text-[#FF66C4]" size={24} />
            <span className="text-sm text-zinc-500">Total Articles</span>
          </div>
          <p className="text-3xl font-bold">{stats.summary.total_articles}</p>
          <p className="text-sm text-zinc-400 mt-1">
            {stats.summary.published_articles} published, {stats.summary.draft_articles} drafts
          </p>
        </div>

        <div className="bg-white border border-zinc-200 p-6" data-testid="stat-period-articles">
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="text-[#FF66C4]" size={24} />
            <span className="text-sm text-zinc-500">In This Period</span>
          </div>
          <p className="text-3xl font-bold">{stats.summary.articles_in_period}</p>
          <p className="text-sm text-zinc-400 mt-1">articles written</p>
        </div>

        <div className="bg-white border border-zinc-200 p-6" data-testid="stat-total-views">
          <div className="flex items-center gap-3 mb-2">
            <Eye className="text-[#FF66C4]" size={24} />
            <span className="text-sm text-zinc-500">Total Views</span>
          </div>
          <p className="text-3xl font-bold">{stats.summary.total_views.toLocaleString()}</p>
          <p className="text-sm text-zinc-400 mt-1">in this period</p>
        </div>

        <div className="bg-white border border-zinc-200 p-6" data-testid="stat-avg-views">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="text-[#FF66C4]" size={24} />
            <span className="text-sm text-zinc-500">Avg Views/Article</span>
          </div>
          <p className="text-3xl font-bold">{stats.summary.avg_views_per_article}</p>
          <p className="text-sm text-zinc-400 mt-1">per article</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Publishing Trend */}
        <div className="bg-white border border-zinc-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="text-[#FF66C4]" size={20} />
            <h3 className="font-semibold">Publishing Trend</h3>
          </div>
          <MiniChart
            data={stats.publishing_trend}
            dataKey="articles"
            color="bg-[#FF66C4]"
            label="articles"
          />
        </div>

        {/* Views Trend */}
        <div className="bg-white border border-zinc-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="text-[#FF66C4]" size={20} />
            <h3 className="font-semibold">Views Trend</h3>
          </div>
          <MiniChart
            data={stats.views_trend}
            dataKey="views"
            color="bg-emerald-500"
            label="views"
          />
        </div>
      </div>

      {/* Category Breakdown */}
      {stats.category_breakdown && stats.category_breakdown.length > 0 && (
        <div className="bg-white border border-zinc-200 p-6">
          <h3 className="font-semibold mb-4">Articles by Category</h3>
          <div className="flex flex-wrap gap-3">
            {stats.category_breakdown.map((cat) => (
              <div
                key={cat.category}
                className="px-4 py-2 bg-zinc-50 border border-zinc-200"
              >
                <span className="font-medium">{cat.category}</span>
                <span className="ml-2 text-[#FF66C4] font-bold">{cat.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Performing Articles */}
      <div className="bg-white border border-zinc-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="text-[#FF66C4]" size={20} />
          <h3 className="font-semibold">Top Performing Articles</h3>
        </div>

        {stats.top_articles && stats.top_articles.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="top-articles-table">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="text-left py-3 px-2 text-sm font-semibold text-zinc-600">
                    #
                  </th>
                  <th className="text-left py-3 px-2 text-sm font-semibold text-zinc-600">
                    Title
                  </th>
                  <th className="text-left py-3 px-2 text-sm font-semibold text-zinc-600">
                    Category
                  </th>
                  <th className="text-left py-3 px-2 text-sm font-semibold text-zinc-600">
                    Status
                  </th>
                  <th className="text-right py-3 px-2 text-sm font-semibold text-zinc-600">
                    Views
                  </th>
                  <th className="text-left py-3 px-2 text-sm font-semibold text-zinc-600">
                    Published
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats.top_articles.map((article, index) => (
                  <tr
                    key={article.slug}
                    className="border-b border-zinc-100 hover:bg-zinc-50"
                  >
                    <td className="py-3 px-2">
                      {index < 3 ? (
                        <span
                          className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                            index === 0
                              ? "bg-yellow-100 text-yellow-700"
                              : index === 1
                              ? "bg-zinc-200 text-zinc-700"
                              : "bg-orange-100 text-orange-700"
                          }`}
                        >
                          {index + 1}
                        </span>
                      ) : (
                        <span className="text-zinc-400 text-sm pl-2">
                          {index + 1}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-2">
                      <a
                        href={`/article/${article.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm hover:text-[#FF66C4] transition-colors line-clamp-1"
                      >
                        {article.title}
                      </a>
                    </td>
                    <td className="py-3 px-2">
                      <span className="text-xs px-2 py-1 bg-zinc-100 text-zinc-600">
                        {article.category}
                      </span>
                    </td>
                    <td className="py-3 px-2">
                      <span
                        className={`text-xs px-2 py-1 ${
                          article.status === "published"
                            ? "bg-green-100 text-green-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {article.status}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="font-semibold text-[#FF66C4]">
                        {article.views.toLocaleString()}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-sm text-zinc-500">
                      {new Date(article.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-zinc-500 text-center py-8">
            No articles found for this writer
          </p>
        )}
      </div>
    </div>
  );
}
